import type { ReactNode } from "react";

/** Signed-out page frame: brand lockup above one card. */
export function AuthShell({
  title,
  subtitle,
  children,
  footer,
  wide,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="relative min-h-screen overflow-hidden bg-bf-bg px-4 text-bf-text">
      {/* Ambient brand glow, echoes the marketing hero. */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-[-12%] h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-brand-purple/25 blur-[120px]" />
        <div className="absolute right-[-6%] top-1/3 h-[360px] w-[360px] rounded-full bg-brand-fuchsia/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[8%] h-[360px] w-[360px] rounded-full bg-brand-deep/25 blur-[120px]" />
      </div>

      <div className="grid min-h-screen place-items-center py-10">
        <div className={"w-full animate-fade-up " + (wide ? "max-w-md" : "max-w-sm")}>
          <div className="mb-8 flex flex-col items-center text-center">
            <img
              src="/logo-neural-eye.svg"
              alt="IntraScene"
              className="h-16 w-16 drop-shadow-[0_6px_22px_rgba(124,58,237,0.5)]"
              width={64}
              height={64}
            />
            <h1 className="mt-4 flex items-center text-3xl font-extrabold tracking-tight text-white">
              Intra<span className="text-gradient">Scene</span>
              <span className="ml-2 rounded-md bg-gradient-to-r from-brand-purple to-brand-fuchsia px-1.5 py-0.5 text-xs font-extrabold uppercase leading-none text-white">
                AI
              </span>
            </h1>
            <p className="mt-2 text-sm text-bf-muted">
              Deep understanding of{" "}
              <span className="text-gradient font-semibold">every scene.</span>
            </p>
          </div>

          <div className="bf-gradient-border">
            <div className="bf-gradient-border-inner p-6 sm:p-7">
              <h2 className="text-lg font-semibold text-white">{title}</h2>
              {subtitle && <p className="mt-1 text-sm text-bf-muted">{subtitle}</p>}
              <div className="mt-6">{children}</div>
            </div>
          </div>

          {footer && <div className="mt-6 space-y-2 text-center text-sm text-bf-muted">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

export function AuthField({
  label,
  hint,
  ...input
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-bf-muted">{label}</span>
      <input className="bf-input" {...input} />
      {hint && <span className="mt-1 block text-xs text-bf-muted">{hint}</span>}
    </label>
  );
}

export function AuthError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
      {message}
    </div>
  );
}
